import { AiMark } from 'components/ai/AiMark';
import { CheckIcon, CircleAlert, CircleCheck, WrenchIcon } from 'components/icons';
import {
    Trace,
    TraceCollapsible,
    TraceCollapsibleContent,
    TraceCollapsibleTrigger,
    TraceDescription,
    TraceIcon,
    TraceLine,
    TraceTitle,
} from 'components/ui/trace';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { AiToolCallHistoryItem, PortalChatMessage } from 'types/aiChat';
import { cn } from 'utils/cn';
import { useHasPermissions } from 'utils/permissionUtil';
import { useAiChatOptionPick } from '../../aiChatOptionPickContext';
import { Spinner } from './Spinner';
import ToolCallCards from './ToolCallCards';
import { doneLabel, runningLabel, stepChip, stepsOf, toolName, turnSeconds } from './toolSteps';

const DEBUG_PERMISSION = ['READ.CRESPECT_AI_DEBUG'];
// The thinking-states swap: the old line leaves (--think-swap), a beat (--think-gap), then the new one rises.
const THINK_SWAP_MS = 200;

/** The status line's text, swapped by the thinking-states recipe when it changes. */
function StatusText({ label }: { label: string }) {
    const [shown, setShown] = useState(label);
    const [entering, setEntering] = useState(false);
    const leaving = label !== shown;

    useEffect(() => {
        if (!leaving) {
            return undefined;
        }
        const timer = setTimeout(() => {
            setShown(label);
            setEntering(true);
        }, THINK_SWAP_MS);
        return () => clearTimeout(timer);
    }, [label, leaving]);

    useEffect(() => {
        if (!entering) {
            return undefined;
        }
        // Placed at its start for a frame, then let go, so it has a state to rise from.
        const frame = requestAnimationFrame(() => setEntering(false));
        return () => cancelAnimationFrame(frame);
    }, [entering]);

    return (
        <span className="t-think text-meta text-start font-medium">
            <span className="t-think-sizer" aria-hidden="true">
                {shown}
            </span>
            <span className={cn('t-think-text', leaving && 'is-exit', entering && 'is-enter-start')} data-text={shown}>
                {shown}
            </span>
        </span>
    );
}

/** A running step's seconds, counted here; only the digits that changed re-enter (number pop-in). */
function StepSeconds() {
    const [seconds, setSeconds] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setSeconds((value) => value + 1), 1000);
        return () => clearInterval(timer);
    }, []);
    const digits = String(seconds);
    return (
        <TraceDescription className="text-caption text-muted-foreground">
            <span className="t-digit-group is-animating" aria-hidden="true">
                {[...digits].map((digit, index) => (
                    // Keyed by place and value, so an unchanged digit keeps its node and does not animate again.
                    <span key={`${digits.length - index}-${digit}`} className="t-digit">
                        {digit}
                    </span>
                ))}
                &nbsp;s
            </span>
        </TraceDescription>
    );
}

function Step({ step, isStreaming, rail }: { step: AiToolCallHistoryItem; isStreaming: boolean; rail: boolean }) {
    const intl = useIntl();
    // A step that finishes in view draws its check in; one that was already done does not animate.
    const [seenRunning] = useState(step.status === 'pending');
    const running = step.status === 'pending' && isStreaming;
    const chip = step.status === 'success' ? stepChip(intl, step) : null;

    return (
        <TraceLine
            role="listitem"
            status={
                step.status === 'error'
                    ? 'error'
                    : step.status === 'pending'
                      ? running
                          ? 'running'
                          : 'pending'
                      : undefined
            }
            className="text-meta text-foreground data-[status=error]:text-danger-ink relative w-full max-w-none gap-2.5"
        >
            {rail ? (
                <span aria-hidden="true" className="bg-border absolute start-[9.5px] top-6 -bottom-3 w-px" />
            ) : null}
            <TraceIcon className="grid size-5 place-items-center">
                {step.status === 'error' ? (
                    <CircleAlert className="size-4" />
                ) : step.status === 'success' ? (
                    <span className={cn('text-success-ink', seenRunning && 't-success-check')} data-state="in">
                        <CheckIcon className="size-4" />
                    </span>
                ) : running ? (
                    <Spinner />
                ) : null}
            </TraceIcon>
            <TraceTitle
                className={cn('flex-1', step.status === 'pending' ? 'font-medium' : 'text-foreground-secondary')}
            >
                {step.status === 'pending'
                    ? runningLabel(intl, step)
                    : step.status === 'error'
                      ? toolName(intl, step.name)
                      : doneLabel(intl, step)}
            </TraceTitle>
            {running ? <StepSeconds /> : null}
            {step.status === 'error' ? (
                <TraceDescription className="text-caption text-danger-ink">
                    {intl.formatMessage({ id: 'ai-chat.tool-call.status.error' })}
                </TraceDescription>
            ) : null}
            {chip ? (
                <TraceDescription className="bg-neutral-tint text-caption text-foreground-secondary rounded-lg px-1.5 font-medium">
                    {chip}
                </TraceDescription>
            ) : null}
        </TraceLine>
    );
}

interface Props {
    message: PortalChatMessage;
    toolCalls: AiToolCallHistoryItem[];
    isStreaming: boolean;
    /** The answer's text has started arriving. */
    hasText: boolean;
}

/**
 * An answer's live progress: while the turn runs, a status line naming the running step and the steps under it;
 * once it ends, the steps fold behind "Worked for {s} s · {n} steps". A user with the AI debug permission sees one
 * card per tool call in place of the steps. Each new running step is announced through the conversation's status
 * region, and so is a reply's end: its summary, or that Crespect AI replied where it ran no step.
 */
export default function AiChatProgress({ message, toolCalls: streamed, isStreaming, hasText }: Props) {
    const intl = useIntl();
    const { announce } = useAiChatOptionPick();
    const debug = useHasPermissions(DEBUG_PERMISSION);
    const [open, setOpen] = useState(false);

    // A call the model started runs once its input is written; one still unwritten when the turn ends never ran.
    const toolCalls = isStreaming ? streamed : streamed.filter((toolCall) => toolCall.state !== 'input-streaming');
    const steps = stepsOf(toolCalls);
    const seconds = turnSeconds(message.metadata);
    const running = isStreaming ? steps.findLast((step) => step.status === 'pending') : undefined;
    const statusLabel = running
        ? runningLabel(intl, running)
        : intl.formatMessage({ id: hasText ? 'ai-chat.progress.writing' : 'ai-chat.progress.working' });
    const summary =
        !isStreaming && seconds !== null && steps.length > 0
            ? intl.formatMessage({ id: 'ai-chat.progress.worked-for' }, { seconds, steps: steps.length })
            : null;

    const announcedStep = useRef<string | null>(null);
    useEffect(() => {
        if (running && announcedStep.current !== running.id) {
            announcedStep.current = running.id;
            announce(statusLabel);
        }
    }, [running, statusLabel, announce]);

    // Once per reply, as the turn ends with an answer; never the answer's text itself.
    const wasStreaming = useRef(isStreaming);
    useEffect(() => {
        if (wasStreaming.current && !isStreaming && hasText) {
            announce(summary ?? intl.formatMessage({ id: 'ai-chat.replied' }));
        }
        wasStreaming.current = isStreaming;
    }, [isStreaming, hasText, summary, announce, intl]);

    const statusLine = isStreaming ? (
        <div className="flex items-center gap-2">
            <AiMark size="status" />
            <StatusText label={statusLabel} />
        </div>
    ) : null;

    if (debug) {
        if (toolCalls.length === 0) {
            return statusLine;
        }
        return (
            <div className="flex flex-col gap-3">
                {statusLine}
                {summary ? (
                    <p className="text-meta text-foreground-tertiary m-0 flex items-center gap-1.5">
                        <WrenchIcon className="size-4" />
                        {summary}
                    </p>
                ) : null}
                <ToolCallCards
                    toolCalls={toolCalls}
                    isStreaming={isStreaming}
                    toolDebug={message.metadata?.toolDebug}
                />
            </div>
        );
    }

    if (steps.length === 0) {
        return statusLine;
    }

    const list = (
        <Trace role="list" className="gap-3">
            {steps.map((step, index) => (
                <Step key={step.id} step={step} isStreaming={isStreaming} rail={index < steps.length - 1} />
            ))}
        </Trace>
    );

    return (
        <div className="flex flex-col gap-3">
            {statusLine}
            {/* Open while the turn runs; folded behind its summary once it ends, which opens it again. */}
            <TraceCollapsible open={!summary || open} onOpenChange={setOpen}>
                {summary ? (
                    <TraceCollapsibleTrigger className="text-meta text-foreground-tertiary gap-1.5 py-0.5 transition-[color,scale] [transition-duration:var(--duration-quick),var(--press-dur)] ease-out motion-safe:will-change-transform motion-safe:active:scale-(--press-scale)">
                        <CircleCheck className="text-success-ink size-4" />
                        {summary}
                    </TraceCollapsibleTrigger>
                ) : null}
                <TraceCollapsibleContent className={cn('mb-0 ml-0 border-l-0 pr-0 pl-0', summary ? 'mt-2.5' : 'mt-0')}>
                    {list}
                </TraceCollapsibleContent>
            </TraceCollapsible>
        </div>
    );
}
